/**
 * /developers/sdk-docs — operator route for feature FS-228.
 *
 * Declares a TanStack Router route and renders `SdkDocsPage`. Root
 * wire-up mounts this from `router.tsx` after the prod-main merge.
 *
 * `?q=` seeds the endpoint search box, so an operator can jump in
 * from an external link like `/developers/sdk-docs?q=clicks`.
 */

import { createRoute, Link } from "@tanstack/react-router";
import { rootRoute } from "./__root";
import { SdkDocsPage } from "@/features/sdk-docs/SdkDocsPage";

interface DevelopersSdkDocsSearch {
	q?: string;
}

export const developersSdkDocsRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/developers/sdk-docs",
	validateSearch: (
		raw: Record<string, unknown>,
	): DevelopersSdkDocsSearch => {
		const v = raw.q;
		if (typeof v === "string" && v.trim().length > 0) {
			return { q: v.trim() };
		}
		return {};
	},
	component: DevelopersSdkDocsRoute,
});

export default developersSdkDocsRoute;

export function DevelopersSdkDocsRoute() {
	const { q } = developersSdkDocsRoute.useSearch();

	return (
		<div className="flex flex-col">
			<nav
				aria-label="Breadcrumb"
				className="mx-auto w-full max-w-6xl px-6 pt-10 text-sm text-zinc-500 sm:pt-16"
			>
				<ol className="flex flex-wrap items-center gap-1">
					<li>
						<Link
							to="/"
							className="rounded-[6px] px-1 py-0.5 hover:bg-zinc-100 hover:text-zinc-900"
						>
							Dashboard
						</Link>
					</li>
					<li aria-hidden="true" className="text-zinc-300">
						/
					</li>
					<li>
						<span className="rounded-[6px] px-1 py-0.5">Developers</span>
					</li>
					<li aria-hidden="true" className="text-zinc-300">
						/
					</li>
					<li className="text-zinc-900">SDK docs</li>
				</ol>
			</nav>

			<SdkDocsPage initialQuery={q} />
		</div>
	);
}

// Named export for the root wire-up cycle.
export { DevelopersSdkDocsRoute as DevelopersSdkDocsPage };
